import React, { useState, useRef, useEffect } from 'react';
import IconTooltip from '../../../common/icon_tooltip/iconTooltip';
import { TriangleAlert, CircleAlert, FileQuestion, EyeOff } from 'lucide-react';
import { WarningTypes} from '../../dataProcessing';
import './warningModal.css';

interface WarningModalProps {
    enableWarnings?: boolean;
    // Passes a stable identifier (rule key + its type) rather than bound function
    // references, so the consumer can always re-derive the live filterFunction/
    // groupKeyFunction from current warningData instead of a stale snapshot.
    onWarningFilter?: (key: string, description: string, type: WarningTypes) => void;
    warningData?: any;
    onClearFiltersAndSorts?: () => void;
    onResetWarning?: () => void;
    // Gates the "Suppressed" filter icon — only relevant for admins reconciling
    // ignored warnings, so it's hidden entirely for everyone else.
    canViewSuppressed?: boolean;
}

const WarningModal: React.FC<WarningModalProps> = ({
    enableWarnings = false,
    onWarningFilter,
    warningData,
    onClearFiltersAndSorts,
    onResetWarning,
    canViewSuppressed = false,
}) => {

    const [showWarningsModal, setShowWarningsModal] = useState(false);
    const [showAlertsModal, setShowAlertsModal] = useState(false);
    const [showPdfAlertsModal, setShowPdfAlertsModal] = useState(false);
    const [showSuppressedModal, setShowSuppressedModal] = useState(false);

    const [activeWarning, setActiveWarning] = useState<number | null>(null);
    const [activeAlert, setActiveAlert] = useState<number | null>(null);
    const [activePdfAlert, setActivePdfAlert] = useState<number | null>(null);
    const [activeSuppressed, setActiveSuppressed] = useState<number | null>(null);
    const [modalPosition, setModalPosition] = useState({ top: 0, right: 0 });

    const alertIconRef = useRef<HTMLDivElement>(null);
    const warningIconRef = useRef<HTMLDivElement>(null);
    const pdfWarningIconRef = useRef<HTMLDivElement>(null);
    const suppressedIconRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    // Reset warning/alert selection from parent
    useEffect(() => {
        if (onResetWarning) {
            (window as any).resetWarningSelection = () => {
                setActiveWarning(null);
                setActiveAlert(null);
                setActivePdfAlert(null);
                setActiveSuppressed(null);
            };
        }
        return () => {
            delete (window as any).resetWarningSelection;
        };
    }, [onResetWarning]);

    // Calculate modal position - unified for all types
    const calculateModalPosition = (iconRef: React.RefObject<HTMLDivElement>) => {
        if (!iconRef.current) return;

        const iconRect = iconRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const modalHeight = 240;
        const modalWidth = 300;

        let top = iconRect.bottom + 8;
        let right = viewportWidth - iconRect.right;

        // Check if modal would go below viewport
        if (top + modalHeight > viewportHeight) {
            top = iconRect.top - modalHeight - 8;
        }

        // Check if modal would go off left edge
        if (iconRect.right - modalWidth < 8) {
            right = viewportWidth - iconRect.left - modalWidth - 8;
        }

        // Check if modal would go off right edge
        if (right < 8) {
            right = 8;
        }

        setModalPosition({ top, right });
    };

    // Update position when modals open
    useEffect(() => {
        if (showWarningsModal) {
            calculateModalPosition(warningIconRef);
        }
    }, [showWarningsModal]);

    useEffect(() => {
        if (showAlertsModal) {
            calculateModalPosition(alertIconRef);
        }
    }, [showAlertsModal]);

    useEffect(() => {
        if (showPdfAlertsModal) {
            calculateModalPosition(pdfWarningIconRef);
        }
    }, [showPdfAlertsModal]);

    useEffect(() => {
        if (showSuppressedModal) {
            calculateModalPosition(suppressedIconRef);
        }
    }, [showSuppressedModal]);

    const handleShowModal = (type: WarningTypes) => {
        if (type === 'alert' && warningData?.hasAlerts) {
            setShowAlertsModal(!showAlertsModal);
            setShowWarningsModal(false);
            setShowPdfAlertsModal(false);
            setShowSuppressedModal(false);

        } else if (type === 'warning' && warningData?.hasWarnings) {
            setShowAlertsModal(false);
            setShowWarningsModal(!showWarningsModal);
            setShowPdfAlertsModal(false);
            setShowSuppressedModal(false);

        } else if (type === 'pdf_alert' && warningData?.hasPdfAlerts) {
            setShowWarningsModal(false);
            setShowAlertsModal(false);
            setShowPdfAlertsModal(!showPdfAlertsModal);
            setShowSuppressedModal(false);

        } else if (type === 'suppressed' && warningData?.hasSuppressed) {
            setShowWarningsModal(false);
            setShowAlertsModal(false);
            setShowPdfAlertsModal(false);
            setShowSuppressedModal(!showSuppressedModal);
        }
    };

    const handleItemClick = (item: any, index: number, type: WarningTypes) => {
        if (!onWarningFilter) return;

        const isActive = type === 'alert' ? activeAlert === index :
            type === 'warning' ? activeWarning === index :
            type === 'suppressed' ? activeSuppressed === index :
            activePdfAlert === index;

        if (isActive) {
            // Deselect and clear filters
            onClearFiltersAndSorts?.();
            if (type === 'alert') {
                setActiveAlert(null);
            } else if (type === 'warning') {
                setActiveWarning(null);
            } else if (type === 'pdf_alert') {
                setActivePdfAlert(null);
            } else if (type === 'suppressed') {
                setActiveSuppressed(null);
            }
        } else {
            // Select and apply filter
            onWarningFilter(item.key, item.description, type);
            if (type === 'alert') {
                setActiveAlert(index);
                setActiveWarning(null); // Clear other types
                setActivePdfAlert(null);
                setActiveSuppressed(null);
            } else if (type === 'warning') {
                setActiveWarning(index);
                setActiveAlert(null); // Clear other types
                setActivePdfAlert(null);
                setActiveSuppressed(null);
            } else if (type === 'pdf_alert') {
                setActivePdfAlert(index);
                setActiveAlert(null); // Clear other types
                setActiveWarning(null);
                setActiveSuppressed(null);
            } else if (type === 'suppressed') {
                setActiveSuppressed(index);
                setActiveAlert(null); // Clear other types
                setActiveWarning(null);
                setActivePdfAlert(null);
            }
        }

        // Close modals
        setShowAlertsModal(false);
        setShowWarningsModal(false);
        setShowPdfAlertsModal(false);
        setShowSuppressedModal(false);
    };

    // Close modal when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const clickedOutsideModal = modalRef.current && !modalRef.current.contains(target);
            const clickedOutsideWarningIcon = warningIconRef.current && !warningIconRef.current.contains(target);
            const clickedOutsideAlertIcon = alertIconRef.current && !alertIconRef.current.contains(target);
            const clickedOutsidePdfAlertIcon = pdfWarningIconRef.current && !pdfWarningIconRef.current.contains(target);
            const clickedOutsideSuppressedIcon = suppressedIconRef.current && !suppressedIconRef.current.contains(target);

            if (clickedOutsideModal && clickedOutsideWarningIcon && clickedOutsideAlertIcon && clickedOutsidePdfAlertIcon && clickedOutsideSuppressedIcon) {
                setShowWarningsModal(false);
                setShowAlertsModal(false);
                setShowPdfAlertsModal(false);
                setShowSuppressedModal(false);
            }
        };

        if (showWarningsModal || showAlertsModal || showPdfAlertsModal || showSuppressedModal) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showWarningsModal, showAlertsModal, showPdfAlertsModal, showSuppressedModal]);

    // Render modal items
    const renderModalItems = (items: any[], activeIndex: number | null, type: WarningTypes) => (
        <div className="warningModal-content">
            {items.map((item: any, index: number) => (
                <div 
                    key={index}
                    className={`warningModal-item ${activeIndex === index ? 'warningModal-selected' : ''}`}
                    onClick={() => handleItemClick(item, index, type)}
                >
                    <div className="warningModal-description">
                        {item.description} ({item.affectedRowsCount})
                    </div>
                </div>
            ))}
        </div>
    );
    
    if (!enableWarnings) {
        return null;
    }

    const hasActiveAlert = activeAlert !== null;
    const hasActiveWarning = activeWarning !== null;
    const hasActivePdfAlert = activePdfAlert !== null;
    const hasActiveSuppressed = activeSuppressed !== null;

    return (
        <div className="warningModal-mainContainer">
            {/* Alerts Icon */}
            <div ref={alertIconRef}>
                <IconTooltip
                    icon={CircleAlert}
                    counter={warningData?.alertCounter}
                    showTooltip={false}
                    disabled={!warningData?.hasAlerts}
                    onClick={() => handleShowModal('alert')}
                    iconColor={warningData?.hasAlerts ? 'var(--color-warning-red)' : 'var(--color-grey-dark)'}
                    isActive={hasActiveAlert}
                />
            </div>

            {/* Warnings Icon */}
            <div ref={warningIconRef}>
                <IconTooltip
                    icon={TriangleAlert}
                    counter={warningData?.warningCounter}
                    showTooltip={false}
                    disabled={!warningData?.hasWarnings}
                    onClick={() => handleShowModal('warning')}
                    iconColor={warningData?.hasWarnings ? 'var(--color-warning-yellow)' : 'var(--color-grey-dark)'}
                    isActive={hasActiveWarning}
                />
            </div>

            {/* PDF Icon */}
            <div ref={pdfWarningIconRef}>
                <IconTooltip
                    icon={FileQuestion}
                    counter={warningData?.pdfAlertCounter}
                    showTooltip={false}
                    disabled={!warningData?.hasPdfAlerts}
                    onClick={() => handleShowModal('pdf_alert')}
                    iconColor={warningData?.hasPdfAlerts ? 'var(--color-red-pdf)' : 'var(--color-grey-dark)'}
                    isActive={hasActivePdfAlert}
                />
            </div>

            {/* Suppressed Icon — admin-only reconciliation filter */}
            {canViewSuppressed && (
                <div ref={suppressedIconRef}>
                    <IconTooltip
                        icon={EyeOff}
                        counter={warningData?.suppressedCounter}
                        showTooltip={false}
                        disabled={!warningData?.hasSuppressed}
                        onClick={() => handleShowModal('suppressed')}
                        iconColor={warningData?.hasSuppressed ? 'var(--color-grey-dark)' : 'var(--color-grey-light-text)'}
                        isActive={hasActiveSuppressed}
                    />
                </div>
            )}

            {/* Warnings Modal */}
            {showWarningsModal && warningData?.hasWarnings && (
                <div 
                    ref={modalRef}
                    className="warningModal-modal"
                    style={{
                        top: `${modalPosition.top}px`,
                        right: `${modalPosition.right}px`
                    }}
                >
                    {renderModalItems(warningData.warningsByRule, activeWarning, 'warning')}
                </div>
            )}

            {/* Alerts Modal */}
            {showAlertsModal && warningData?.hasAlerts && (
                <div 
                    ref={modalRef}
                    className="warningModal-modal"
                    style={{
                        top: `${modalPosition.top}px`,
                        right: `${modalPosition.right}px`
                    }}
                >
                    {renderModalItems(warningData.alertsByRule, activeAlert, 'alert')}
                </div>
            )}

            {/* PDF Alerts Modal */}
            {showPdfAlertsModal && warningData?.hasPdfAlerts && (
                <div 
                    ref={modalRef}
                    className="warningModal-modal"
                    style={{
                        top: `${modalPosition.top}px`,
                        right: `${modalPosition.right}px`
                    }}
                >
                    {renderModalItems(warningData.pdfAlertsByRule, activePdfAlert, 'pdf_alert')}
                </div>
            )}

            {/* Suppressed Modal */}
            {showSuppressedModal && warningData?.hasSuppressed && (
                <div
                    ref={modalRef}
                    className="warningModal-modal"
                    style={{
                        top: `${modalPosition.top}px`,
                        right: `${modalPosition.right}px`
                    }}
                >
                    {renderModalItems(warningData.suppressedByRule, activeSuppressed, 'suppressed')}
                </div>
            )}
        </div>
    );
};

export default WarningModal;